import { priceAllocation } from '@/features/bidding/endpoints/bids'
import type { ExtendedBid } from '@/features/bidding/types/bid'
import type { BidSaveTransformer } from '@/features/bidding/types/bidOfferPriceFlow'
import type { AggregatedBid } from '@/features/bidding/utils/model/buildAggregatedBid'
import type { AggregatedBidWrapper } from '@/features/bidding/utils/model/mapActivationGroupBidsToAggregatedBids'
import { cloneAggregatedBid, cloneExtendedBids } from '@/features/bidding/utils/price/cloneBidData'
import {
  comparePriceAllocations,
  logPriceAllocationComparison,
} from '@/features/bidding/utils/price/comparePriceAllocations'
import { splitAggregatedBid } from '@/features/bidding/utils/price/splitAggregatedBid'
import { updateExtendedBidsBasedOnAggregatedBids } from '@/features/bidding/utils/price/updateExtendedBidsBasedOnAggregatedBids'

/**
 * Transformer for aggregated flow
 *
 * Converts an aggregated bid back into individual activation group bids
 * by splitting volumes proportionally.
 */
export const aggregatedBidTransformer: BidSaveTransformer<{
  aggregatedBid: AggregatedBid
  draftBids: ExtendedBid[]
}> = async ({ aggregatedBid, draftBids }) => {
  return await priceAllocation(
    // Clone data to prevent mutations affecting the shadow comparison
    cloneAggregatedBid(aggregatedBid),
    cloneExtendedBids(draftBids),
  ).then((backendResult) => {
    // Compare with front-end allocation
    const frontendResult = splitAggregatedBid(aggregatedBid, draftBids)
    logPriceAllocationComparison(comparePriceAllocations(frontendResult, backendResult))
    return frontendResult
  })
}

/**
 * Transformer for by-activation-group flow
 *
 * Updates the original ExtendedBid objects with new price chunks
 * from the user-modified aggregated bids.
 */
export const byActivationGroupBidTransformer: BidSaveTransformer<{
  bidsPerActivationGroup: Map<string, AggregatedBidWrapper>
  bidsWithPrices: ExtendedBid[]
}> = ({ bidsPerActivationGroup, bidsWithPrices }) => {
  return updateExtendedBidsBasedOnAggregatedBids(bidsWithPrices, bidsPerActivationGroup)
}
